import React, { JSX, useEffect, useRef, useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import AuthRequired from './shared/authentication/request-authentication';
import Home from './pages/Home';
import About from './pages/About';
import Configuracion from './pages/Configuracion';
import KanbanBoard from './kanban/kanvan.page';
import { Inbox } from './inbox/Inbox';
import FileExplorer from './explorer/FileExplorer';
import ActividadesPage from './pages/actividades/actividades-page';
import { requestNotificationPermission, onMessageListener } from './config/firebase';
import Manager from './manager/Manager';
import { useAppDispatch, useAppSelector } from './store';
import { getSession, clearAuth } from './shared/authentication/reducers/authentication';
import { FaInbox } from '@react-icons/all-files/fa/FaInbox';
import { FaColumns } from '@react-icons/all-files/fa/FaColumns';
import { FaFolder } from '@react-icons/all-files/fa/FaFolder';
import { FaClipboardList } from '@react-icons/all-files/fa/FaClipboardList';
import { FaUserShield } from '@react-icons/all-files/fa/FaUserShield';
import { FaBell } from '@react-icons/all-files/fa/FaBell';
import { FaUserCircle } from '@react-icons/all-files/fa/FaUserCircle';
import { FaTachometerAlt } from '@react-icons/all-files/fa/FaTachometerAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaSignOutAlt } from '@react-icons/all-files/fa/FaSignOutAlt';
import { FaBars } from '@react-icons/all-files/fa/FaBars';
import { FaInfoCircle } from '@react-icons/all-files/fa/FaInfoCircle';
import { FaSearch } from '@react-icons/all-files/fa/FaSearch';

export const hasAnyAuthority = (authorities: string[], hasAnyAuthorities: string[]) => {
  if (authorities && authorities.length !== 0) {
    if (hasAnyAuthorities.length === 0) return true;
    return hasAnyAuthorities.some(auth => authorities.includes(auth));
  }
  return false;
};

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { to: '/',           label: 'Dashboard',    icon: <FaTachometerAlt /> },
  { to: '/inbox',      label: 'Inbox',        icon: <FaInbox /> },
  { to: '/kanban',     label: 'Kanban',       icon: <FaColumns /> },
  { to: '/explorer',   label: 'Drive',        icon: <FaFolder /> },
  { to: '/actividades',label: 'Actividades',  icon: <FaClipboardList /> },
  { to: '/manager',       label: 'Manager',       icon: <FaUserShield />, adminOnly: true },
  { to: '/configuracion', label: 'Configuración', icon: <FaCog /> },
  { to: '/about',         label: 'Acerca',        icon: <FaInfoCircle /> },
];

function SidebarItem({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <Link to={item.to} className={`crm-nav-item${active ? ' active' : ''}`}>
      <span className="crm-nav-icon">{item.icon}</span>
      <span className="crm-nav-label">{item.label}</span>
    </Link>
  );
}

function UserAvatar({ userId, className, size = 24 }: { userId?: number | string; className?: string; size?: number }) {
  const [errored, setErrored] = useState(false);
  if (!errored && userId) {
    return (
      <img
        src={`/public/usuario/${userId}/image.png`}
        alt="avatar"
        className={className}
        width={size}
        height={size}
        onError={() => setErrored(true)}
        style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return <FaUserCircle className={className} style={{ fontSize: size, flexShrink: 0 }} />;
}

function UserMenu({ userId, login, firstName, lastName, onLogout }: { userId?: number | string; login: string; firstName?: string; lastName?: string; onLogout: () => void }) {
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || login || 'Cuenta';
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="crm-user-pill"
        onClick={() => setOpen(v => !v)}
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <UserAvatar userId={userId} className="crm-user-avatar" size={40} />
        <span className="crm-user-name">{displayName}</span>
      </button>

      {open && (
        <div style={menuStyles.dropdown}>
          <Link to="/account" style={menuStyles.item} onClick={() => setOpen(false)}>
            <UserAvatar userId={userId} size={15} />
            Cuenta
          </Link>
          <div style={menuStyles.separator} />
          <button style={{ ...menuStyles.item, ...menuStyles.logoutItem }} onClick={onLogout}>
            <FaSignOutAlt style={menuStyles.itemIcon} />
            Cerrar Sesión
          </button>
        </div>
      )}
    </div>
  );
}

const menuStyles: Record<string, React.CSSProperties> = {
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
    minWidth: 180,
    zIndex: 200,
    overflow: 'hidden',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 500,
    color: '#1e293b',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.12s',
  },
  logoutItem: {
    color: '#ef4444',
  },
  itemIcon: {
    fontSize: 15,
    flexShrink: 0,
  },
  separator: {
    height: 1,
    background: '#f1f5f9',
    margin: '2px 0',
  },
};

function Main() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notification, setNotification] = useState<any>(null);
  const [token, setToken] = useState<string | null>(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);
  const interceptorRef = useRef<number | null>(null);

  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const sessionHasBeenFetched = useAppSelector(state => state.authentication.sessionHasBeenFetched);
  const sessionLoading = useAppSelector(state => state.authentication.loading);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, ['ROLE_ADMIN']));
  const account = useAppSelector(state => state.authentication.account);

  // Global axios interceptor: on 401/403 clear auth state → shows login page
  useEffect(() => {
    interceptorRef.current = axios.interceptors.response.use(
      response => response,
      error => {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          dispatch(clearAuth());
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [dispatch]);

  // Fetch session on mount
  useEffect(() => { dispatch(getSession()); }, []);

  // No auto-redirect — show the login page instead (handled below)

  useEffect(() => {
    const unsubscribe = onMessageListener((payload) => {
      setNotification(payload);
      if (Notification.permission === 'granted') {
        new Notification(payload.notification?.title || 'Notificación', {
          body: payload.notification?.body,
          icon: payload.notification?.icon || '/logo192.png',
        });
      }
    });
    return () => {};
  }, []);

  const handleEnableNotifications = async () => {
    setNotifLoading(true);
    setNotifError(null);
    const result = await requestNotificationPermission();
    if (result.success && result.token) {
      setToken(result.token);
    } else {
      setNotifError(result.error || 'Error desconocido');
      if (result.errorType === 'brave-blocked') alert(result.error);
    }
    setNotifLoading(false);
  };

  useEffect(() => { handleEnableNotifications(); }, []);

  // Show full-screen spinner while GET /api/account is in-flight
  if (!sessionHasBeenFetched) {
    return (
      <div style={loadingStyles.shell}>
        <div style={loadingStyles.spinner} />
        <p style={loadingStyles.text}>Verificando sesión…</p>
      </div>
    );
  }

  // Session fetched but user is not authenticated → show the login page
  if (sessionHasBeenFetched && !isAuthenticated) return <AuthRequired />;

  // Still waiting for the session check (shouldn't reach here, spinner above handles it)
  if (!isAuthenticated) return null;

  const visibleNavItems = navItems.filter(item => !item.adminOnly || isAdmin);
  const currentLabel = visibleNavItems.find(item =>
    item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)
  )?.label ?? 'Dashboard';

  return (
    <div className="crm-shell">
      {/* ── Sidebar ── */}
      <aside className={`crm-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>
        <div className="crm-sidebar-header">
          <img src="/logo.png" alt="Logo" width="32" height="32" className="crm-logo-img" />
          {!sidebarCollapsed && <span className="crm-brand">Profase CRM</span>}
        </div>

        <nav className="crm-nav">
          {visibleNavItems.map(item => (
            <SidebarItem
              key={item.to}
              item={item}
              active={item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)}
            />
          ))}
        </nav>

        <div className="crm-sidebar-footer">
          {isAdmin && (
            <a href="/admin" className="crm-nav-item">
              <span className="crm-nav-icon"><FaCog /></span>
              {!sidebarCollapsed && <span className="crm-nav-label">Admin</span>}
            </a>
          )}
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="crm-main">
        {/* Top Header */}
        <header className="crm-topbar">
          <div className="crm-topbar-left">
            <button className="crm-toggle-btn" onClick={() => setSidebarCollapsed(v => !v)} title="Toggle sidebar">
              <FaBars />
            </button>
            <span className="crm-page-title">{currentLabel}</span>
          </div>

          <div className="crm-topbar-center">
            <div className="crm-search-box">
              <FaSearch className="crm-search-icon" />
              <input type="text" placeholder="Buscar en CRM..." className="crm-search-input" />
            </div>
          </div>

          <div className="crm-topbar-right">
            <button className="crm-icon-btn" title="Notificaciones">
              <FaBell />
              {notification && <span className="crm-badge" />}
            </button>
            <UserMenu
              userId={account?.id}
              login={account?.login ?? ''}
              firstName={account?.firstName}
              lastName={account?.lastName}
              onLogout={async () => {
                try { await axios.get('/auth/logout'); } catch (_) {}
                dispatch(clearAuth());
              }}
            />
          </div>
        </header>

        {/* Page content */}
        <main className="crm-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/kanban" element={<KanbanBoard />} />
            <Route path="/explorer" element={<FileExplorer />} />
            <Route path="/actividades" element={<ActividadesPage />} />
            {isAdmin && <Route path="/manager/*" element={<Manager />} />}
            <Route path="/configuracion/*" element={<Configuracion />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default Main;

const loadingStyles: Record<string, React.CSSProperties> = {
  shell: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f2f5',
    gap: 16,
  },
  spinner: {
    width: 40,
    height: 40,
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  text: {
    margin: 0,
    fontSize: 14,
    color: '#64748b',
  },
};
